import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Download, AlertTriangle, Link2, ArrowUpDown, ArrowUp, ArrowDown, FileSignature } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocaleFormat } from "../../i18n/useLocaleFormat";
import DocTypeIcon, { getCategoryMeta } from "./DocTypeIcon";
import DocStatusPill, { deriveDocStatus } from "./DocStatusPill";
import DocumentRowActionsMenu from "./DocumentRowActionsMenu";

/**
 * Documents table — enterprise-grade row layout designed to handle
 * hundreds of documents without losing alignment or readability.
 *
 * Columns (desktop, ≥1024px):
 *   DOCUMENT │ CATÉGORIE │ STATUT │ RISQUES │ ÉCHÉANCE │ ACTIONS
 *
 * On tablet (768-1024px) the CATÉGORIE column folds under DOCUMENT.
 *
 * All cells use `truncate` + `title` to preserve every character on hover
 * and never break the column grid — financial figures stay tabular-nums.
 *
 * Props:
 *   docs              : array of document records from /api/documents
 *   riskCounts        : { [doc_id]: number } — active alert count per doc
 *   nextDueByDoc      : { [doc_id]: iso_date_string } — closest deadline
 *   loading           : boolean
 *   onActionView, onActionDownload, onActionMore : callbacks
 */
export default function DocumentsTable({
  docs = [],
  riskCounts = {},
  nextDueByDoc = {},
  loading = false,
  onRefresh,
  selectedIds = new Set(),
  onToggleOne = () => {},
  onToggleAll = () => {},
}) {
  const { t } = useTranslation("documents");
  const { formatDate } = useLocaleFormat();
  const navigate = useNavigate();
  // Sort state — only the Échéance column is currently sortable. Values:
  //   null  → preserve incoming order (created_at DESC)
  //   "asc" → soonest deadline first (docs without a deadline pushed to the end)
  //   "desc"→ latest deadline first  (docs without a deadline pushed to the end)
  const [dueSort, setDueSort] = useState(null);

  const sortedDocs = useMemo(() => {
    if (!dueSort) return docs;
    const out = [...docs];
    out.sort((a, b) => {
      const da = nextDueByDoc[a.id];
      const db_ = nextDueByDoc[b.id];
      if (!da && !db_) return 0;
      if (!da) return 1;       // missing deadlines sink to the bottom
      if (!db_) return -1;
      return dueSort === "asc" ? (da < db_ ? -1 : da > db_ ? 1 : 0)
                                : (da < db_ ? 1 : da > db_ ? -1 : 0);
    });
    return out;
  }, [docs, nextDueByDoc, dueSort]);

  const cycleDueSort = () => {
    setDueSort((cur) => (cur === null ? "asc" : cur === "asc" ? "desc" : null));
  };

  const visibleIds = useMemo(() => sortedDocs.map((d) => d.id), [sortedDocs]);
  const selectedVisibleCount = visibleIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;
  const headerCheckboxRef = useRef(null);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  if (loading) {
    return (
      <div data-testid="documents-table-loading" className="px-5 py-10">
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-14 rounded-lg bg-zinc-50 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }
  if (!docs.length) {
    return (
      <div data-testid="documents-table-empty" className="px-5 py-14 text-center">
        <p className="text-sm text-zinc-500">{t("table.empty")}</p>
      </div>
    );
  }

  return (
    <div data-testid="documents-table" className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[820px]">
        <colgroup>
          <col style={{ width: "4%" }} />
          <col style={{ width: "40%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "8%" }} />
        </colgroup>
        <thead>
          <tr className="border-b border-zinc-100">
            <th scope="col" className="px-5 py-3 w-[4%]">
              <input
                ref={headerCheckboxRef}
                type="checkbox"
                checked={allVisibleSelected}
                onChange={() => onToggleAll(visibleIds)}
                data-testid="doc-select-all"
                aria-label={t("bulk.select_all_visible")}
                className="h-4 w-4 rounded border-zinc-300 accent-zinc-950 cursor-pointer"
              />
            </th>
            {["document", "category", "status", "risks", "due", "actions"].map((col) => {
              const isDue = col === "due";
              const baseCls = `text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-semibold px-5 py-3 ${
                col === "risks" || col === "actions" ? "text-right" : "text-left"
              }`;
              if (!isDue) {
                return (
                  <th key={col} scope="col" className={baseCls}>
                    {t(`table.col_${col}`)}
                  </th>
                );
              }
              const SortIcon = dueSort === "asc" ? ArrowUp
                : dueSort === "desc" ? ArrowDown : ArrowUpDown;
              return (
                <th key={col} scope="col" className={baseCls}>
                  <button
                    type="button"
                    onClick={cycleDueSort}
                    data-testid="documents-due-sort-btn"
                    className={`inline-flex items-center gap-1 transition-colors ${
                      dueSort ? "text-qenva-navy" : "hover:text-qenva-navy"
                    }`}
                    title={t("table.col_due_sort_tooltip", { defaultValue: "Trier par échéance" })}
                  >
                    {t(`table.col_${col}`)}
                    <SortIcon size={11} strokeWidth={2} />
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedDocs.map((d) => {
            const meta = getCategoryMeta(d.document_type);
            const status = deriveDocStatus(d);
            const riskCount = riskCounts[d.id] || 0;
            const dueIso = nextDueByDoc[d.id];
            const dueDate = dueIso ? new Date(dueIso) : null;
            const isInactive = status === "inactive";
            return (
              <tr
                key={d.id}
                data-testid={`documents-row-${d.id}`}
                onClick={() => navigate(`/analysis/${d.id}`)}
                className={`group cursor-pointer border-b border-zinc-100 last:border-b-0 transition-colors ${
                  isInactive ? "bg-qenva-off/40 hover:bg-qenva-off/70" : "hover:bg-qenva-off/50"
                }`}
              >
                <td className="px-5 py-4 align-middle" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(d.id)}
                    onChange={() => onToggleOne(d.id)}
                    data-testid={`doc-select-${d.id}`}
                    aria-label={t("bulk.select_one", { name: d.filename })}
                    className="h-4 w-4 rounded border-zinc-300 accent-zinc-950 cursor-pointer"
                  />
                </td>
                {/* DOCUMENT */}
                <td className="px-5 py-4 align-middle min-w-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <DocTypeIcon docType={d.document_type} size={36} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <p
                          className={`text-[14px] font-semibold tracking-tight truncate ${
                            isInactive ? "text-zinc-400" : "text-qenva-navy"
                          }`}
                          title={d.filename}
                        >
                          {d.filename}
                        </p>
                        <LineageBadge doc={d} />
                        <PreSignatureBadge doc={d} />
                      </div>
                      {/* Category fallback on tablet (≤md) where the dedicated column is hidden */}
                      <p className="md:hidden text-[11px] text-zinc-500 mt-0.5 truncate">
                        {t(`category.${(d.document_type || "contract").toLowerCase()}`, { defaultValue: d.document_type })}
                      </p>
                    </div>
                  </div>
                </td>

                {/* CATÉGORIE — hidden below md */}
                <td className="hidden md:table-cell px-5 py-4 align-middle min-w-0">
                  <span
                    className={`text-[13px] truncate inline-block max-w-full ${meta.colorClass} font-medium`}
                    title={t(`category.${(d.document_type || "contract").toLowerCase()}`, { defaultValue: d.document_type })}
                  >
                    {t(`category.${(d.document_type || "contract").toLowerCase()}`, { defaultValue: d.document_type })}
                  </span>
                </td>

                {/* STATUT */}
                <td className="px-5 py-4 align-middle">
                  <DocStatusPill status={status} />
                </td>

                {/* RISQUES — right-aligned numeric */}
                <td className="px-5 py-4 align-middle text-right tabular-nums">
                  {riskCount > 0 ? (
                    <span
                      data-testid={`risk-count-${d.id}`}
                      className="inline-flex items-center gap-1 text-[13px] font-semibold text-qenva-cat-contract"
                      title={t("table.risk_count_tooltip", { count: riskCount })}
                    >
                      <AlertTriangle size={13} strokeWidth={2} />
                      {riskCount}
                    </span>
                  ) : (
                    <span className="text-zinc-300 text-[13px]">—</span>
                  )}
                </td>

                {/* ÉCHÉANCE */}
                <td className="px-5 py-4 align-middle">
                  {dueDate ? (
                    <span
                      className="text-[13px] text-qenva-navy tabular-nums truncate inline-block max-w-full"
                      title={formatDate(dueDate, { year: "numeric", month: "long", day: "numeric" })}
                    >
                      {formatDate(dueDate, { year: "numeric", month: "short", day: "numeric" })}
                    </span>
                  ) : (
                    <span className="text-zinc-300 text-[13px]">—</span>
                  )}
                </td>

                {/* ACTIONS */}
                <td className="px-5 py-4 align-middle text-right">
                  <div
                    className="inline-flex items-center gap-1.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActionBtn
                      label={t("actions.preview")}
                      Icon={Eye}
                      testId={`action-view-${d.id}`}
                      onClick={() => navigate(`/analysis/${d.id}`)}
                    />
                    <ActionBtn
                      label={t("actions.download")}
                      Icon={Download}
                      testId={`action-download-${d.id}`}
                      href={d.report_url || `/api/documents/${d.id}/report`}
                      download
                    />
                    <DocumentRowActionsMenu doc={d} onRefresh={onRefresh} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LineageBadge({ doc }) {
  const navigate = useNavigate();
  const { t } = useTranslation("documents");
  const meta = doc?.lineage_meta || {};
  const chainLength = meta.chain_length || 1;
  if (!meta.status || chainLength <= 1) return null;

  // For the badge target: link to the base of the chain (chain_root_id),
  // falling back to the doc itself if the chain root is missing.
  const targetId = meta.chain_root_id || doc.id;
  const linkedCount = Math.max(0, chainLength - 1);
  const label = t("table.lineage_badge", {
    count: linkedCount,
    defaultValue: `+${linkedCount} liés`,
  });

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/lineage/${targetId}`);
      }}
      title={t("table.lineage_badge_tooltip", {
        defaultValue: "Voir la chaîne documentaire",
      })}
      data-testid={`lineage-badge-${doc.id}`}
      className="inline-flex items-center gap-1 shrink-0 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10.5px] font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
    >
      <Link2 size={10} strokeWidth={2.2} />
      {label}
    </button>
  );
}


function PreSignatureBadge({ doc }) {
  const { t } = useTranslation("documents");
  if (doc?.document_category !== "pre_signature") return null;

  return (
    <span
      title={t("table.pre_signature_badge_tooltip", {
        defaultValue: "Document pré-signature",
      })}
      data-testid={`pre-signature-badge-${doc.id}`}
      className="inline-flex items-center gap-1 shrink-0 rounded-full bg-qenva-blue-soft border border-qenva-blue/25 px-2 py-0.5 text-[10.5px] font-semibold text-qenva-blue"
    >
      <FileSignature size={10} strokeWidth={2.2} />
      {t("table.pre_signature_badge", { defaultValue: "Pré-signature" })}
    </span>
  );
}

function ActionBtn({ Icon, label, onClick, href, download, testId }) {
  const cls =
    "w-7 h-7 grid place-items-center rounded-md text-zinc-400 hover:text-qenva-navy hover:bg-zinc-100 transition-colors";
  if (href) {
    return (
      <a href={href} download={download} title={label} aria-label={label} className={cls} data-testid={testId}>
        <Icon size={14} strokeWidth={1.75} />
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cls}
      data-testid={testId}
    >
      <Icon size={14} strokeWidth={1.75} />
    </button>
  );
}
